/**
 * Analytics Tracker - Lightweight client-side event tracking
 * Uses sendBeacon for non-blocking, reliable event delivery
 */
import { Capacitor } from '@capacitor/core';

const BASE_URL = Capacitor.isNativePlatform() ? 'https://www.stellarskyimager.fr' : '';
const ANALYTICS_ENDPOINT = `${BASE_URL}/api/analytics`;

// [US 6.28] Durée de vie de l'identifiant de mesure d'audience : 13 mois (13 × 30 jours) à compter de sa
// création, sans prolongation à chaque visite (recommandation CNIL pour l'exemption de consentement).
// Même durée que la rétention serveur (ANALYTICS_RETENTION_DAYS).
const VISITOR_ID_TTL_MS = 390 * 86_400_000;
// Identifiants créés avant la mémorisation de leur date : supposés créés au plus tôt possible
// (visitorId introduit en janvier 2026), pour ne jamais dépasser les 13 mois.
const LEGACY_VISITOR_ID_CREATED = Date.UTC(2026, 0, 1);

export function getVisitorId(now: number = Date.now()): string {
    const STORAGE_KEY = 'ssi_visitor_id';
    const CREATED_KEY = 'ssi_visitor_id_created';
    let vid = localStorage.getItem(STORAGE_KEY);
    const created = Number(localStorage.getItem(CREATED_KEY)) || (vid ? LEGACY_VISITOR_ID_CREATED : 0);
    if (!vid || now - created >= VISITOR_ID_TTL_MS) {
        const oldId = vid;
        vid = crypto.randomUUID ? crypto.randomUUID() : 'vx-' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem(STORAGE_KEY, vid);
        localStorage.setItem(CREATED_KEY, String(now));
        // Appareil admin exclu des statistiques : le serveur reporte l'exclusion sur le nouvel identifiant.
        if (oldId) postJson(`${ANALYTICS_ENDPOINT}/visitor-renewed`, JSON.stringify({ oldId, newId: vid }));
    } else if (!localStorage.getItem(CREATED_KEY)) {
        localStorage.setItem(CREATED_KEY, String(created));
    }
    return vid;
}

/**
 * Track an analytics event
 * @param eventType - Type of event (e.g., 'page_view', 'planner_calculate', 'pdf_export')
 * @param eventData - Optional additional data for the event
 */
export function trackEvent(eventType: string, eventData?: Record<string, unknown>): void {
    try {
        const payload = JSON.stringify({
            eventType,
            eventData: {
                ...(eventData || {}),
                visitorId: getVisitorId(),
                platform: Capacitor.isNativePlatform() ? 'android-app' : 'web'
            }
        });

        postJson(ANALYTICS_ENDPOINT, payload);
    } catch {
        // Silent fail
    }
}

function postJson(url: string, payload: string): void {
    try {
        // Use sendBeacon for non-blocking delivery (works even on page unload)
        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(url, blob);
        } else {
            // Fallback for browsers without sendBeacon
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
            }).catch(() => {
                // Silent fail - don't break user experience
            });
        }
    } catch {
        // Silent fail
    }
}

const VALID_PUBLIC_ROUTES = [
    '/',
    '/galerie',
    '/actualites',
    '/news',
    '/evenements',
    '/events',
    '/materiel',
    '/about',
    '/help',
    '/a-propos',
    '/contact',
    '/videos',
    '/ressources',
    '/planner',
    '/guides',
    '/guide-planification',
    '/guide-fov',
    '/guide-filtres',
    '/confidentialite',
    '/mentions-legales',
    '/cgv',
    '/suppression-compte',
    '/horizon-generator',
    '/exifs-acquisition',
    '/sentinel',
    '/temoignages',
    '/decouverte-planner'
];

// Routes applicatives connues, volontairement NON mesurées : la mesure d'audience ne couvre que
// le site public (cf. politique de confidentialité). Les lister évite de logger un avertissement
// « route inconnue » à chaque visite — le warning doit rester réservé aux vraies routes inconnues
// (scans de bots type /collections/all), pour lesquelles il a une valeur de diagnostic.
const SILENT_UNTRACKED_ROUTES = [
    '/login',
    '/register',
    '/account',
    '/verify-email',
    '/forgot-password',
    '/reset-password',
    '/founder-confirm',
    '/sentinel/tonight',
    '/sentinel/projects',
    '/sentinel/calendar',
    '/sentinel/alerts'
];

/**
 * Track a page view (convenience wrapper)
 * @param path - The page path (defaults to current location)
 * @param overrideLang - Optional language override (useful during language changes)
 */
export function trackPageView(path?: string, overrideLang?: string): void {
    const currentPath = path || window.location.pathname;

    // Filter out unknown routes (likely bots like /collections/all)
    if (!VALID_PUBLIC_ROUTES.includes(currentPath)) {
        // Les routes applicatives connues sont ignorées en silence (cf. SILENT_UNTRACKED_ROUTES) ;
        // `/sentinel/projects/:id` est couverte par le test de préfixe.
        const knownUntracked = SILENT_UNTRACKED_ROUTES.some(
            (r) => currentPath === r || currentPath.startsWith(`${r}/`)
        );
        if (!knownUntracked) {
            console.warn(`[Analytics] Skipping track for unknown route: ${currentPath}`);
        }
        return;
    }

    // Priority: overrideLang -> localStorage (i18next) -> document lang -> default 'fr'
    const i18nextLng = window.localStorage.getItem('i18nextLng') || '';
    const lang = (overrideLang || i18nextLng || document.documentElement.lang || 'fr').split('-')[0].toLowerCase();
    const nightMode = document.documentElement.classList.contains('night-vision');

    trackEvent('page_view', {
        path: currentPath,
        referrer: document.referrer || undefined,
        title: document.title,
        lang,
        nightMode
    });
}

/**
 * Track planner calculation
 * @param location - Location name used
 * @param lat - Latitude
 * @param lon - Longitude
 * @param targetCount - Number of targets returned
 * @param extraData - Optional additional data (top targets, equipment, etc.)
 */
export function trackPlannerCalculation(
    location: string,
    lat: number,
    lon: number,
    targetCount: number,
    extraData?: {
        topTargets?: string[];  // Top 5 target names
        focalLength?: number;
        aperture?: number;
        filterType?: string;
        trackingMode?: number;
        catalogFilters?: string[];  // Catalogues sélectionnés (messier, ngc, ic, sh2, arp, abell)
        calculationTime?: number; // Duration in ms
        instrumentBrand?: string; // NEW
        instrumentModel?: string; // NEW
        instrumentType?: string;  // NEW
        bortle?: number;         // NEW: Bortle class
        sessionDuration?: string; // NEW: 1h, 2h, 4h, all-night
        continuousMode?: boolean; // NEW: Mode continu (US 3.1)
        moonPhaseKey?: string;   // NEW: Clé de phase lunaire (newMoon, waxingCrescent, etc.)
    }
): void {
    trackEvent('planner_calculate', {
        location,
        lat,
        lon,
        targetCount,
        ...(extraData || {})
    });
}

/**
 * Track planner error
 * @param error - Error message or object
 */
export function trackPlannerError(error: string): void {
    trackEvent('planner_error', {
        error: typeof error === 'string' ? error : JSON.stringify(error)
    });
}

/**
 * Métadonnées d'une cible exportée, permettant à la home de régénérer une image DSS
 * pour les cibles hors catalogue local (ex: recherche manuelle type LBN472).
 * Le `name` doit correspondre à la valeur poussée dans `topTargets`.
 */
export interface ExportTargetMeta {
    name: string;
    ra: number;   // degrés
    dec: number;  // degrés
    size?: number; // arcminutes
}

/**
 * Track PDF export
 * @param targetCount - Number of targets in the PDF
 * @param topTargets - Array of target names included in the PDF
 * @param targetsMeta - Coordonnées des cibles (fallback image home hors catalogue)
 */
export function trackPdfExport(targetCount: number, topTargets?: string[], targetsMeta?: ExportTargetMeta[]): void {
    trackEvent('pdf_export', {
        targetCount,
        topTargets,
        targetsMeta
    });
}

/**
 * Track gallery view
 * @param photoId - Optional specific photo viewed
 */
export function trackGalleryView(photoId?: string): void {
    trackEvent('gallery_view', {
        photoId
    });
}

/**
 * Track video play
 * @param videoId - YouTube video ID
 * @param title - Video title
 */
export function trackVideoPlay(videoId: string, title?: string): void {
    trackEvent('video_play', {
        videoId,
        title
    });
}

/**
 * Track resource download
 * @param resourceId - Resource ID
 * @param resourceName - Resource name
 */
export function trackResourceDownload(resourceId: string, resourceName?: string): void {
    trackEvent('resource_download', {
        resourceId,
        resourceName
    });
}

/**
 * Track configuration export
 * @param includesCustomTargets - Whether custom targets were included in the export
 */
export function trackConfigExport(includesCustomTargets?: boolean): void {
    trackEvent('config_export', {
        includesCustomTargets
    });
}

/**
 * Track acquisition export (NINA, ASIAIR)
 * @param format - Export format ('nina-json', 'nina-xml', 'asiair-csv')
 * @param targetCount - Number of targets exported
 * @param topTargets - Optional list of target names for trending stats
 */
export function trackAcquisitionExport(format: string, targetCount: number, topTargets?: string[], targetsMeta?: ExportTargetMeta[]): void {
    trackEvent('acquisition_export', {
        format,
        targetCount,
        topTargets,
        targetsMeta
    });
}

/**
 * Track configuration import
 * @param includesCustomTargets - Whether custom targets were included in the import
 */
export function trackConfigImport(includesCustomTargets?: boolean, success: boolean = true): void {
    trackEvent('config_import', {
        includesCustomTargets,
        success
    });
}

/**
 * Track share modal open
 */
export function trackShareModalOpen(): void {
    trackEvent('share_modal_open');
}

/**
 * Track share image generation
 */
export function trackShareImageGenerate(): void {
    trackEvent('share_image_generate');
}

/**
 * Track share image download
 */
export function trackShareImageDownload(): void {
    trackEvent('share_image_download');
}

/**
 * Track AstroBin external link click
 * @param catalogName - Catalog name passed to AstroBin search
 */
export function trackAstroBinClick(catalogName: string): void {
    trackEvent('astrobin_click', {
        catalogName
    });
}

/**
 * Track user language change
 * @param lang - New language code
 */
export function trackLanguageChange(lang: string): void {
    trackEvent('language_change', { lang });
}

/**
 * Track night mode toggle
 * @param enabled - Whether night mode is enabled or disabled
 */
export function trackNightModeToggle(enabled: boolean): void {
    trackEvent('night_mode_toggle', { enabled });
}

/**
 * Track Horizon Generator export (.hrz file downloaded or saved)
 */
export function trackHorizonExport(): void {
    trackEvent('horizon_export');
}

/**
 * Track Horizon Generator profile sent to Planner
 */
export function trackHorizonSendToPlanner(): void {
    trackEvent('horizon_send_to_planner');
}

/**
 * [US 6.14 prep] Track a click on a Sentinel landing CTA (funnel visite → CTA → inscription).
 * @param placement - Where the CTA sits (hero, pricing, finalCta)
 */
export function trackSentinelCtaClick(placement: string): void {
    trackEvent('sentinel_cta_click', { placement });
}

// ============================================
// REAL-TIME PRESENCE (Heartbeat)
// ============================================

// ============================================

const HEARTBEAT_ENDPOINT = `${BASE_URL}/api/heartbeat`;
const HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds

let heartbeatIntervalId: ReturnType<typeof setInterval> | null = null;

/**
 * Send a heartbeat to indicate the user is still active
 */
function sendHeartbeat(): void {
    try {
        const payload = JSON.stringify({
            visitorId: getVisitorId()
        });

        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(HEARTBEAT_ENDPOINT, blob);
        } else {
            fetch(HEARTBEAT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true
            }).catch(() => { /* Silent fail */ });
        }
    } catch {
        // Silent fail
    }
}

/**
 * Start the heartbeat interval
 * Called automatically when the module loads
 */
export function startHeartbeat(): void {
    if (heartbeatIntervalId) return; // Already running

    // Don't track admin pages
    if (window.location.pathname.startsWith('/admin')) return;

    // Send initial heartbeat
    sendHeartbeat();

    // Start interval
    heartbeatIntervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            sendHeartbeat();
        }
    });
}

/**
 * Stop the heartbeat interval
 */
export function stopHeartbeat(): void {
    if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
        heartbeatIntervalId = null;
    }
}

// Auto-start heartbeat when module loads (only in browser, not on admin pages)
if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
    startHeartbeat();
}


